import { SkeletonMesh, SkeletonData } from '@esotericsoftware/spine-threejs';
import { MeshProps, Vector2 as V2 } from '@react-three/fiber';
import { Vector2 } from 'three';
import { DamselConfig as Config } from '../types/damsel';
import { Tool, ToolLocation } from './ToolFactory';

type DamselConfig = Record<keyof Config, string>
type DamselConfigInput = Partial<DamselConfig>

export type MeshOptions = Omit<MeshProps, 'position' | 'scale'> & {
  position?: V2,
  scale?: number
}


const defaultOffset = [0, -300]
const defaultScale = 0.66
const defaultZ = 0

const defaultMeshOptions = {
  position: [0, 0] as V2,
  scale: 1
}

const animationTracks = {
  'body': 0,
  'eyes': 1
}

const convertV2toV3 = (vec: V2, offset: number[]): [number, number, number] => {
  if(typeof vec === 'number') {
    return [vec + (offset[0] || 0), 0 + (offset[1] || 0), defaultZ]
  }

  if(vec instanceof Vector2) {
    const arr = vec.toArray()
    return [arr[0] + (offset[0] || 0), arr[1] + (offset[1] || 0), defaultZ]
  }

  return [vec[0] + (offset[0] || 0), vec[1] + (offset[1] || 0), defaultZ]
}


type Relation = Record<string, Record<string, string> | string>

const accessoryRelationships = {
  'mouth': {
    'ballgag': 'gag9',
    'cloth': {
      '-': 'gag6',
      'gag5': 'gag7',
    },
    'panties': 'gag5',
    'tape1': 'gag1',
    'tape2': 'gag4',
    'tape3': 'gag3',
    'tape4': 'gag2',
  },
  'eyes': {
    'cloth': 'blindfold1',
    'tape1': 'blindfold2',
    'tape2': 'blindfold3',
    'tape3': 'blindfold4',
  }
} as Partial<Record<ToolLocation, Relation>>


const toolAccessoryMap = {
  'mouth': 'gag',
  'eyes': 'blindfold'
} as Record<ToolLocation, keyof DamselConfig>

type Equipment = {
  [K in ToolLocation]?: Tool[]
}
export default class Damsel extends SkeletonMesh {

  private config: DamselConfigInput
  private meshOptions: MeshOptions = defaultMeshOptions
  private equippedTools: Equipment = {}

  constructor(skeletonData: SkeletonData, damselConfig: DamselConfigInput, meshOptions?: MeshOptions) {
    console.log('creating damsel...')
    super(skeletonData, (parameters) => {
      parameters.depthTest = false;
      parameters.alphaTest = 0.5;
    })

    this.config = damselConfig

    this.setMeshOptions(meshOptions)
    this.initDamsel()
  }


  private initDamsel() {
    this.applyConfig()
  }

  private applyConfig(_config?: DamselConfigInput) {

    const config = {...this.config, ...(_config || {})}
    this.config = config

    const baseSkin = this.skeleton.data.findSkin(`body/damsel/${config.damsel}`)
    const newSkin = this.skeleton.skin || baseSkin;
    
      // console.log({baseSkin, newSkin})
    
      if(!newSkin) {
        console.log('error')
        return
      }
    
      let configKey: keyof DamselConfig
      
      for(configKey in config) {
    
        const name = configKey
        const skinName = config[configKey]
    
        const skins = this.skeleton.data.skins.filter(s => s.name.includes(name))
    
        if(name !== 'damsel' && skinName) {
          const accessorySkin = this.skeleton.data.skins.find(s => s.name.includes(`${name}/${skinName}`))
    
          // console.log({name, skinName, skins, accessorySkin})
    
          skins.filter(s => !s.name.includes(skinName)).forEach(s => {
            //.filter(a => !(a.attachment instanceof PointAttachment)) removed
            s.getAttachments().forEach(a => {
              newSkin.removeAttachment(a.slotIndex, a.name)
            })
          })
          
          accessorySkin && newSkin.addSkin(accessorySkin)
    
        } else if(name !== 'damsel') {
          skins.forEach(s => {
            s.getAttachments().forEach(a => {
              newSkin.removeAttachment(a.slotIndex, a.name)
              // if(a.attachment instanceof PointAttachment) {
              //   const newAttach = baseSkin?.getAttachment(a.slotIndex, a.name)
              //   newAttach && newSkin.setAttachment(a.slotIndex, a.name, newAttach)
              // }
            })
          })
        }
    
    
      }
    
    
      this.skeleton.setSkin(newSkin);
      this.skeleton.setSlotsToSetupPose();
      this.state.apply(this.skeleton)
    
      
  
    
  }

  public setConfig(config: DamselConfigInput) {
    this.config = { ...this.config, ...config }
  }
  public setMeshOptions(meshOptions?: MeshOptions) {
    if(!meshOptions) {
      return
    }
    this.meshOptions = { ...this.meshOptions, ...meshOptions }
  }

  public getConfigValue(key: keyof DamselConfig) {
    return this.config[key] ?? null
  }

  static getOptions(options: MeshOptions) {
    const ops = {...defaultMeshOptions, ...options }

    const position = convertV2toV3(ops.position, defaultOffset)
    const scale = ops.scale * defaultScale

    return {
      ...options,
      position,
      scale
    }
  }

  public getMeshOptions() {
    return Damsel.getOptions(this.meshOptions)
  }

  private updateEquipped(tool: Tool | null, location: ToolLocation) {

    const key = toolAccessoryMap[location] // gag
    const temp = accessoryRelationships[location]?.[tool?.name || ''] || ''
    
    const isNotLayered = typeof temp === 'string'

    const newValue = isNotLayered ? temp : temp[this.config[key] || '-']
    console.log('setting', key, newValue)
    this.applyConfig({ [key]: newValue || '' })
    return !isNotLayered
  }

  public equipTool(tool: Tool, location: ToolLocation) {

    if(!tool.possibleLocations.includes(location)) {
      return false
    }

    const toolAlreadyEquipped = !!this.equippedTools[location]?.some(t => t.name === tool.name)

    if(toolAlreadyEquipped) {
      console.log('already equipped')
      return false
    }

    let retTool: Tool | null = null

    const isLayered = this.updateEquipped(tool, location)

    if(this.equippedTools[location] && isLayered) {
      this.equippedTools[location]?.push(tool)
    } else {
      retTool = this.equippedTools[location]?.pop() || null
      this.equippedTools[location] = [tool]
    }

    return retTool
  }
  
  public unEquipTool(location: ToolLocation) {

    if(this.equippedTools[location]) {
      const retTool = this.equippedTools[location]?.pop() || null
      const t = this.equippedTools[location]?.slice(-1)?.[0] || null
      this.updateEquipped(t, location)
      return retTool
    }
    return null
  }


  public blink() {
    this.state.setAnimation(animationTracks['eyes'], 'blink')
  }
  public idle() {
    this.state.setAnimation(animationTracks['body'], 'isIdle', true)
  }


}